import classNames from "classnames";
import isEmpty from "is-empty";
import moment from "moment";
import React, { useCallback, useState } from "react";
import toast from "react-hot-toast";
import { useSearchParams } from "react-router-dom";
import { addProductToOccupantMutation } from "rsl-api-106/lib/mutations";
import {
  CustomerBooking,
  Discount,
  Lineitem,
  Maybe,
  Mutation,
  MutationAddProductToOccupantArgs,
  ProductDistributionType,
  Stay,
} from "rsl-api-106/lib/types";

import { useApolloClient } from "@apollo/client";
import Button from "@components/_Layout/Controls/Button";
import Popup from "@components/_Layout/Controls/Popup";
import Delimiter from "@components/_Layout/Delimiter";
import StayOccupants from "@components/Occupants/StayOccupants";
import { useCart, useConfig, useSession } from "@hooks";
import {
  CheckCircleOutlineOutlined,
  CloseOutlined,
  DeleteOutlined,
} from "@mui/icons-material";
import { StepsEnum } from "@pages";
import { getCurrencySymbol } from "@utils/currencies";
import dateFormat from "@utils/dateformat";
import { isToday } from "@utils/dates";
import { getAbsoluteImagePath, replaceImgOnError } from "@utils/dom";
import { removeLocalStorageItem } from "@utils/localStorage";

import { COMMISSIONS_PRODUCTS, TAXES_PRODUCTS } from "../../../constants";
import { CurrencyCodeEnum } from "../../../types/enum";

type BasketSummaryProps = {
  reservations: Maybe<CustomerBooking>[] | undefined;
  enableEdit?: boolean;
  border?: boolean;
  title?: string;
  onCloseSlideMenu?: () => void;
  selectedInsurance?: string | undefined;
  setSelectedInsurance?: (value: string | undefined) => void;
};

const languagesVarsCodes = [
  "LANG_neobooking_accommodations", //Hébergements
  "LANG_neobooking_occupant", //Occupant
  "LANG_neobooking_occupants", //Occupants
  "LANG_neobooking_adult", //Adulte
  "LANG_neobooking_adults", //Adultes
  "LANG_neobooking_children", //Enfant
  "LANG_neobooking_childrens", //Enfants
  "LANG_neobooking_baby", //Bébé
  "LANG_neobooking_babies", //Bébés
  "LANG_neobooking_dates", //Dates
  "LANG_neobooking_remove_my_reservation", //Supprimer ma réservation
  "LANG_neobooking_remove_this_stay", //Supprimer ce séjour
  "LANG_neobooking_more_details", //Plus de détails
  "LANG_neobooking_from_date", //Du
  "LANG_neobooking_to_date", //Au
  "LANG_neobooking_reduction", //Réduction
  "LANG_neobooking_total", //Total
  "LANG_neobooking_cart_empty", //Votre panier est vide
  "LANG_neobooking_reduction_successfully_removed", //Réduction supprimée !
  "LANG_neobooking_reservation_successfully_removed", //Réservation supprimée !
  "LANG_neobooking_remove_reduction", //Supprimer la réduction
  "LANG_neobooking_your_code", //Votre code
  "LANG_neobooking_your_code_successfully_applied", //a bien été appliqué sur votre panier
  "LANG_neobooking_pre_reserved_pay_on_site", //(à régler sur place)
  "LANG_neobooking_days", // jours
  "LANG_neobooking_infos_tax", //Ce prix comprend l'hébergement et les charges excepté la taxe de séjour qui doit être réglée sur place.
  "LANG_neobooking_payment_secure", //Paiement sécurisé avec Paybox
  "LANG_neobooking_payment_3x", //Payez en 3x sans frais !
  "LANG_neobooking_edit", //Modifier
  "LANG_neobooking_total_discount_of", //Soit une économie de
];

type StayBaseDiscountProps = {
  stay: Maybe<Stay>;
};

const StayBaseDiscount: React.FC<StayBaseDiscountProps> = ({ stay }) => {
  let discounts: Maybe<Discount>[] = [];
  const { authMode, getLanguageVarValue } = useSession();
  const { mode: clientMode } = useConfig();
  const mode = clientMode ? clientMode : "vacanceole";

  stay?.occupants?.forEach((occupant) => {
    occupant?.lineItems?.forEach((lineitem) => {
      if (
        lineitem?.product?.isBase &&
        lineitem?.discounts &&
        (lineitem?.discounts?.length ?? 0 > 0)
      ) {
        discounts = [...discounts, ...lineitem?.discounts];
      }
    });
  });

  const getCGOSRemise = (): undefined | Lineitem => {
    let remise = undefined;
    if (authMode === 1) {
      if (stay) {
        for (let k = 0; k < (stay.occupants?.length ?? -1); k++) {
          const currentOccupant = stay?.occupants?.[k];
          if (currentOccupant) {
            const occupantLineItems = currentOccupant?.lineItems;
            if (occupantLineItems && occupantLineItems.length > 0) {
              for (let l = 0; l < (occupantLineItems.length ?? -1); l++) {
                const lineitem = occupantLineItems[l];
                if (lineitem && lineitem?.product?.code === "REMEXCGOS") {
                  remise = lineitem;
                }
              }
            }
          }
        }
      }
    }
    return undefined;
    //return remise;
  };

  return (
    <>
      {discounts.map((discount, discountIndex) => {
        return (
          <div
            key={`${discount?.label}_${discountIndex}`}
            className={`tw-flex tw-gap-2 tw-justify-between ${
              mode === "vacanceole"
                ? "tw-text-[#f7b103]"
                : "tw-text_corseole-danger"
            }`}
          >
            <span>
              <span className="tw-font-bold tw-mr-2 tw-opacity-0">1x </span>
              {!isEmpty(discount?.label)
                ? discount?.label
                : getLanguageVarValue("LANG_neobooking_remises")}
            </span>
            <div className="tw-font-semibold tw-flex">
              {(
                discount?.price?.amount +
                (getCGOSRemise() ? getCGOSRemise()?.product?.price?.amount : 0)
              ).toFixed(2)}
              {getCurrencySymbol(discount?.price?.currencyCode!!)}
              <div className="tw-w-7"></div>
            </div>
          </div>
        );
      })}
    </>
  );
};

type TotalBaseDiscountProps = {
  reservation: Maybe<CustomerBooking>;
};
const TotalBaseDiscount: React.FC<TotalBaseDiscountProps> = ({
  reservation,
}) => {
  /* Hooks */
  const { getLanguageVarValue, authMode } = useSession();
  const { mode: clientMode } = useConfig();
  const mode = clientMode ? clientMode : "vacanceole";

  let total = 0;
  let symbol: CurrencyCodeEnum | string = CurrencyCodeEnum.EUR;

  reservation?.stays?.forEach((stay) => {
    stay?.occupants?.forEach((occupant) => {
      occupant?.lineItems?.forEach((lineitem) => {
        if (
          lineitem?.product?.code !== "PARCGOS" &&
          lineitem?.product?.isBase === true
        ) {
          lineitem?.discounts?.forEach((discount) => {
            total += discount?.price?.amount!!;
            symbol = discount?.price?.currencyCode!!;
          });
        }
      });
    });
  });

  const getCGOSRemise = (): undefined | Lineitem => {
    let remise = undefined;
    if (authMode === 1) {
      for (let i = 0; i < (reservation?.stays?.length ?? -1); i++) {
        let stay = reservation?.stays?.[i];
        if (stay) {
          for (let k = 0; k < (stay.occupants?.length ?? -1); k++) {
            const currentOccupant = stay?.occupants?.[k];
            if (currentOccupant) {
              const occupantLineItems = currentOccupant?.lineItems;
              if (occupantLineItems && occupantLineItems.length > 0) {
                for (let l = 0; l < (occupantLineItems.length ?? -1); l++) {
                  const lineitem = occupantLineItems[l];
                  if (lineitem && lineitem?.product?.code === "REMEXCGOS") {
                    remise = lineitem;
                  }
                }
              }
            }
          }
        }
      }
    }
    return remise;
  };

  return Math.abs(total) > 10 ? (
    <div
      className={`${
        mode === "vacanceole" ? "tw-text-[#f7b103]" : "tw-text_corseole-danger"
      } tw-text-right tw-mr-[28px]`}
    >
      {getLanguageVarValue("LANG_neobooking_total_discount_of")}{" "}
      {Math.abs(total).toFixed(2)}
      {getCurrencySymbol(symbol)}
    </div>
  ) : (
    <></>
  );
};

const BasketSummary: React.FC<BasketSummaryProps> = ({
  reservations,
  enableEdit = true,
  border = true,
  title,
  onCloseSlideMenu,
  selectedInsurance,
  setSelectedInsurance,
}) => {
  /* Hooks */
  const { removeDiscountCode, deleteReservation, removeStay } = useCart();
  const { getLanguageVarValue } = useSession();
  const { mode: clientMode } = useConfig();
  const mode = clientMode ? clientMode : "vacanceole";

  const getTotalTax = () => {
    let totalTax = 0;
    for (let i = 0; i < (reservations?.length ?? -1); i++) {
      const currentReservation = reservations?.[i];
      if (currentReservation) {
        for (let j = 0; j < (currentReservation.stays?.length ?? -1); j++) {
          const currentStay = currentReservation?.stays?.[j];
          if (currentStay) {
            for (let k = 0; k < (currentStay.occupants?.length ?? -1); k++) {
              const currentOccupant = currentStay?.occupants?.[k];
              if (currentOccupant) {
                const occupantLineItems = currentOccupant?.lineItems;
                if (occupantLineItems && occupantLineItems.length > 0) {
                  for (let l = 0; l < (occupantLineItems.length ?? -1); l++) {
                    const lineitem = occupantLineItems[l];
                    if (lineitem) {
                      if (
                        TAXES_PRODUCTS.includes(lineitem.product?.code ?? "")
                      ) {
                        totalTax += lineitem.totalAmount?.amount;
                      }
                    }
                  }
                }
              }
            }
          }
        }
      }
    }
    return totalTax;
  };

  const handleRemoveDiscountCode = (callback: () => void) => {
    removeDiscountCode((err) => {
      if (err) toast.error(err.message);
      else
        toast.success(
          getLanguageVarValue("LANG_neobooking_reduction_successfully_removed")
        );
      callback();
    });
  };

  const handleDeleteReservation = (
    reservationCartItemID: number,
    callback: () => void
  ) => {
    deleteReservation(reservationCartItemID, (err) => {
      if (err) toast.error(err.message);
      else
        toast.success(
          getLanguageVarValue(
            "LANG_neobooking_reservation_successfully_removed"
          )
        );
      callback();
    });
  };

  return (
    <div className={`${border ? "tw-border" : ""}`}>
      {reservations ? (
        reservations.map((reservation, index) => {
          return (
            <ReservationBasketSummary
              enableEdit={enableEdit}
              reservation={reservation!!}
              onRemoveDiscountCode={handleRemoveDiscountCode}
              onDeleteReservation={handleDeleteReservation}
              onCloseSlideMenu={onCloseSlideMenu}
              selectedInsurance={selectedInsurance}
              setSelectedInsurance={setSelectedInsurance}
              key={`resabasketsummary_${reservation?.id}_${index}`}
              totalTax={getTotalTax()}
            />
          );
        })
      ) : (
        <>
          <div className="tw-p-2">
            {getLanguageVarValue("LANG_neobooking_cart_empty")}
          </div>
        </>
      )}
    </div>
  );
};

BasketSummary.propTypes = {
  //
};

type ReservationBasketSummaryProps = {
  reservation: CustomerBooking;
  enableEdit: boolean;
  onRemoveDiscountCode: (callback: () => void) => void;
  onDeleteReservation: (
    reservationCartItemID: number,
    callback: () => void
  ) => void;
  onCloseSlideMenu?: () => void;
  selectedInsurance?: string | undefined;
  setSelectedInsurance?: (value: string | undefined) => void;
  totalTax?: number;
};

type NbOccupantByReservationTitleProps = {
  reservation: CustomerBooking;
  pluralAdultText: string;
  singleAdultText: string;
  pluralChildrenText: string;
  singleChildrenText: string;
  pluralBabyText: string;
  singleBabyText: string;
};
export const NbOccupantByReservationTitle: React.FC<NbOccupantByReservationTitleProps> =
  ({
    reservation,
    singleAdultText,
    singleBabyText,
    singleChildrenText,
    pluralAdultText,
    pluralBabyText,
    pluralChildrenText,
  }): JSX.Element => {
    let nbadults = 0;
    let nbchildren = 0;
    reservation?.stays?.forEach((s, sI) => {
      s?.occupants?.forEach((o) => {
        if (o?.isAdult) nbadults += 1;
        if (o?.isChildren1 || o?.isChildren2) nbchildren += 1;
        if (o?.isBaby) nbchildren += 1;
      });
    });

    return (
      <>
        <>
          {nbadults}{" "}
          {nbadults === 1 ? <>{singleAdultText}</> : <>{pluralAdultText}</>}
        </>
        {nbchildren > 0 && (
          <>
            {" - "}{" "}
            <>
              {nbchildren}{" "}
              {nbchildren === 1 ? (
                <>{singleChildrenText}</>
              ) : (
                <>{pluralChildrenText}</>
              )}
            </>
          </>
        )}
      </>
    );
  };

type LineItemRowProps = {
  qte: number;
  totalPrice: any;
  totalPriceWithoutDiscount: any;
  stayGuiid: number;
  occupantIndex: number;
  lineitemIndex: number;
  lineitem: Lineitem;
  occupantGuiid: number;
  handler?: (
    code: string,
    id: number,
    qte: number,
    distributionType: ProductDistributionType,
    stayGuiid: number,
    occupantGuiid: number,
    callback: () => void
  ) => void;
  handler2?: (
    stayGuiid: number,
    lineitem: Lineitem,
    callback: () => void
  ) => void;
  selectedInsurance?: string | undefined;
  setSelectedInsurance?: (value: string | undefined) => void;
};
const LineItemRow: React.FC<LineItemRowProps> = ({
  qte,
  totalPrice,
  totalPriceWithoutDiscount,
  stayGuiid,
  occupantIndex,
  lineitemIndex,
  lineitem,
  occupantGuiid,
  handler,
  handler2,
}) => {
  /* Hooks */
  const { getLanguageVarValue, authMode } = useSession();
  const { mode: clientMode } = useConfig();
  const mode = clientMode ? clientMode : "vacanceole";
  /* States */
  const [isRemovingLineitem, setIsRemovingLineitem] = useState<boolean>(false);

  /* Functions */
  const handleClick = (
    code: string,
    id: number,
    qte: number,
    distributionType: ProductDistributionType,
    occupantGuiid: number
  ) => {
    setIsRemovingLineitem(true);
    if (handler) {
      handler(code, id, qte, distributionType, stayGuiid, occupantGuiid, () => {
        setIsRemovingLineitem(false);
      });
    } else {
      if (handler2) {
        handler2(stayGuiid, lineitem, () => {
          setIsRemovingLineitem(false);
        });
      }
    }
  };

  const hasDiscount = lineitem.discounts && lineitem.discounts.length > 0;

  /* Render */

  if (
    authMode === 1 &&
    lineitem.product?.code ===
      "PARCGOS" /* || lineitem.product?.code === "REMEXCGOS"*/
  ) {
    return null;
  }

  return (
    <div>
      {/* si produit en discount, on affiche la ligne en couleur orange*/}
      <div className={`tw-flex tw-gap-2 tw-justify-between`}>
        <span>
          <span
            className={classNames(
              "tw-font-bold tw-mr-2",
              lineitem.product?.code === "REMISEL" ? "tw-opacity-0" : "",
              hasDiscount && lineitem.product?.isBase
                ? mode === "vacanceole"
                  ? "tw-text-[#f7b103]"
                  : "tw-text_corseole-danger"
                : ""
            )}
          >
            {hasDiscount
              ? `${qte > 0 ? qte : lineitem?.quantity}x`
              : `${qte > 0 ? qte : lineitem?.quantity}x`}
          </span>{" "}
          {hasDiscount && lineitem.product?.isBase ? (
            <span
              className={`${
                mode === "vacanceole"
                  ? "tw-text-[#f7b103]"
                  : "tw-text_corseole-danger"
              }`}
            >
              {lineitem?.discounts?.map((x) => x?.label).join(",")}
            </span>
          ) : (
            <>
              {lineitem?.product?.webLabel
                ? lineitem?.product?.webLabel
                : lineitem?.product?.label}
              {!isEmpty(lineitem.product?.otherLabel) &&
                `: ${lineitem.product?.otherLabel}`}
            </>
          )}
        </span>
        <div className="tw-flex tw-items-center">
          {lineitem?.preReserved && (
            <span>
              {getLanguageVarValue("LANG_neobooking_pre_reserved_pay_on_site")}
            </span>
          )}
          <span
            className={classNames(
              "tw-font-semibold",
              hasDiscount && lineitem.product?.isBase
                ? mode === "vacanceole"
                  ? "tw-text-[#f7b103]"
                  : "tw-text_corseole-danger"
                : ""
            )}
          >
            {totalPrice?.toFixed(2)}
            {getCurrencySymbol(lineitem?.totalAmount?.currencyCode!!)}
          </span>
          {(occupantIndex !== 0 || lineitemIndex !== 0 || occupantIndex > 0) &&
          !lineitem?.product?.mandatory &&
          lineitem.product?.code !== "REMISEL" ? (
            <Button
              onClick={() =>
                handleClick(
                  lineitem?.product?.code!!,
                  lineitem?.product?.id!!,
                  0,
                  lineitem?.product?.distributionType!!,
                  occupantGuiid
                )
              }
              shape="link"
              variant="default"
              isLoading={isRemovingLineitem}
              disabled={isRemovingLineitem}
              additionalClassname="tw-flex tw-ml-2"
            >
              <DeleteOutlined fontSize="small" />
            </Button>
          ) : (
            <div className="tw-w-7"></div>
          )}
        </div>
      </div>
    </div>
  );
};

const ReservationBasketSummary: React.FC<ReservationBasketSummaryProps> = ({
  reservation,
  enableEdit,
  onRemoveDiscountCode,
  onDeleteReservation,
  onCloseSlideMenu,
  selectedInsurance,
  setSelectedInsurance,
  totalTax,
}) => {
  const client = useApolloClient();
  const { sessionId, getLanguageVarValue, authMode } = useSession();
  const { cart, addProductToGlobal, addProductToOccupant, setCart } = useCart();
  const { mode: clientMode } = useConfig();
  const mode = clientMode ? clientMode : "vacanceole";
  const [searchParams, _] = useSearchParams();

  //loading action states
  const [isRemovingCode, setIsRemovingCode] = useState<boolean>(false);
  const [isDeletingReservation, setIsDeletingReservation] =
    useState<boolean>(false);

  const removeDiscountCode = () => {
    setIsRemovingCode(true);
    onRemoveDiscountCode(() => {
      setIsRemovingCode(false);
    });
  };

  const getFidelityTotal = useCallback(() => {
    let total = 0;
    let currencyCode = "";
    reservation.stays?.map((s) => {
      s?.occupants?.[0]?.paymentDues?.map((p) => {
        if (!reservation.id && p?.payments && p.payments.length > 0) {
          p.payments?.map((pa) => {
            if (isToday(new Date(pa?.date))) {
              total += pa?.amount?.amount;
              currencyCode = pa?.amount?.currencyCode ?? "";
            }
          });
        }
      });
    });

    return { total, currencyCode };
  }, [reservation]);

  const handleRemoveProduct = (
    productCode: string,
    productId: number,
    quantity: number,
    distributionType: ProductDistributionType,
    stayGuiid: number,
    occupantGuiid: number,
    callback: () => void
  ) => {
    if (
      distributionType === ProductDistributionType.Global ||
      distributionType === ProductDistributionType.Room
    ) {
      //si une presta assurance est remove, on remove la value dans le localstorage & ont set selectedInsurance a undefined
      if (
        distributionType === ProductDistributionType.Global &&
        productCode.startsWith("FSA")
      ) {
        removeLocalStorageItem("product_insurance");
        setSelectedInsurance && setSelectedInsurance(undefined);
      }
      addProductToGlobal(
        productCode,
        productId,
        quantity,
        (err) => {
          if (err) toast.error(err.message);
          callback();
        },
        stayGuiid
      );
    } else {
      addProductToOccupant(
        productCode,
        productId,
        occupantGuiid,
        quantity,
        (err) => {
          if (err) toast.error(err.message);
          callback();
        }
      );
    }
  };

  const getBaseLineItem = (stayGuiid: number): Lineitem | null => {
    let x = reservation?.stays?.find((s) => s?.guiid === stayGuiid);
    let baseLineItem = null;
    if (x) {
      x.occupants?.forEach((o) => {
        o?.lineItems?.forEach((l) => {
          if (l?.product?.isBase) {
            baseLineItem = l;
          }
        });
      });
    }

    return baseLineItem;
  };

  const isPaiement3X = useCallback(() => {
    //on affiche la ligne paiement 3X si on est pas encore sur l'étape de paiement
    let show = true;
    if (searchParams.get("step") === StepsEnum.PAYMENT.toString()) {
      //si on est dans l'étape de paiement, alors on affiche la ligne paiement 3X si  l'échéancier selectionné est P1X
      show = reservation?.selectedPaymentDue === "P1X";
    }
    return show;
  }, [searchParams.get("step"), cart]);

  const getCancelationDateLimit = useCallback(() => {
    const isetebPartenaire =
      reservation?.property?.cmsCriterias?.find(
        (x) => x?.code === "ANNULPART"
      ) !== undefined;

    const displayCancellation = showCancellation();
    //Date limite d'annulation
    let cancellationDate: Date | null = null;
    if (displayCancellation) {
      //Date de début de la résa
      const reservationStartDate = new Date(reservation.startDate);

      //On calcule le nombre de jours de la résa
      const nbDays = reservation.nbDays ?? 0;

      //On calcule la différence de nombre de jours entre aujourd'hui et la date de début de la résa (startDate)
      const diff = moment(reservationStartDate)
        .startOf("day")
        .diff(moment(new Date()).startOf("day"), "day");

      //Si le nombre de jours de résa est suppérieur a 4 et que la diff de jours est suppérieur a 14, alors on peux cancel
      if (nbDays >= 4) {
        if (diff > 14) {
          cancellationDate = moment(reservationStartDate)
            .startOf("day")
            .subtract("days", 14)
            .toDate();
        }
      }
      //Si le nombre de jours de résa est entre 1 et 3, et que la diff de jours est suppérieur a 3, alors on peux cancel
      if (nbDays >= 1 && nbDays <= 3) {
        if (diff > 3) {
          cancellationDate = moment(reservationStartDate)
            .startOf("day")
            .subtract("days", 4)
            .toDate();
        }
      }
    }
    //return cancellationDate;
    return isetebPartenaire ? null : cancellationDate;
  }, [reservation]);

  const getFormuleSejour = () => {
    let formule = "";
    reservation?.stays?.forEach((s) => {
      s?.occupants?.forEach((o, i) => {
        //on tape sur 1er occupant
        if (i === 0) {
          o?.lineItems?.forEach((l) => {
            if (l?.product?.isBase) {
              formule = l.label ?? "";
            }
          });
        }
      });
    });

    return formule;
  };

  const getRoomTypeCodes = (): string[] => {
    let codes: string[] = [];
    reservation?.stays?.forEach((stay) => {
      stay?.distribution?.roomTypes?.forEach((roomType) => {
        codes = [...codes, roomType?.categoryCode ?? ""];
      });
    });

    return codes;
  };

  const cancellablePrestations = ["LOC", "FC"];
  const onlyPayboxPrestations = ["LOCNR", "FCNR", "NANR"];

  const showCancellation = () => {
    const exist = reservation?.stays?.[0]?.occupants?.[0]?.lineItems?.find(
      (x) =>
        x?.product?.isBase === true &&
        !onlyPayboxPrestations.includes(x.product.code)
    );
    return exist ? true : false;
  };

  const showPaiement3X = () => {
    //On affiche Cancellation si un des codes roomType correspondent a ceux de cancellablePrestations
    const result = getRoomTypeCodes().some((code) => {
      if (onlyPayboxPrestations.includes(code)) {
        return false;
      } else return true;
    });

    return (
      isPaiement3X() &&
      result &&
      reservation?.stays?.[0]?.occupants?.[0]?.paymentDues?.length !== 1
    );
  };

  const getPaymentX = () => {
    let x = reservation?.stays?.[0]?.occupants?.[0]?.paymentDues?.length ?? 2;

    return `LANG_neobooking_payment_${x}x`;
  };

  const getTotalWithFidelity = () => {
    const f = getFidelityTotal().total;
    const t = reservation.totalAmount?.amount;

    return t - f;
  };

  const getDistinctLineItems = (stay: Stay) => {
    const result = getDistinctLineItemsByStay(stay);
    return result;
  };

  const getDistinctLineItemsByStay = (stay: Stay) => {
    const allLinesItems = stay?.occupants?.map((x) => x?.lineItems);
    const allItems = allLinesItems?.flatMap((innerArray) => innerArray);
    const resultArray = allItems?.reduce(
      (accumulator: any, currentItem: any) => {
        const existingItem = accumulator.find(
          (item: any) =>
            item?.product?.code === currentItem?.product?.code &&
            item?.product?.otherCode === currentItem?.product?.otherCode &&
            item?.product?.price?.amount?.toFixed(2) ===
              currentItem?.product?.price?.amount?.toFixed(2)
        );

        if (existingItem) {
          // Augmenter la quantité si un doublon est trouvé + MAJ du prix
          existingItem.qte = (existingItem.qte || 1) + 1;
          existingItem.totalPrice =
            (existingItem.totalPrice || 0) + currentItem?.totalAmount?.amount;
          existingItem.totalPriceWithoutDiscount =
            (existingItem.totalPriceWithoutDiscount || 0) +
            currentItem?.totalAmountWithoutDiscount?.amount;
        } else {
          // Ajouter un nouvel élément à l'accumulateur avec une quantité du lineitem de base + MAJ du prix
          accumulator.push({
            ...currentItem,
            qte: currentItem.quantity,
            totalPrice: currentItem?.totalAmount?.amount,
            totalPriceWithoutDiscount:
              currentItem?.totalAmountWithoutDiscount?.amount,
          });
        }

        return accumulator;
      },
      []
    );
    //console.error(resultArray);
    return resultArray;
  };

  const handleRemoveLineItem = async (
    stayGuiid: number,
    lineitemToRemove: Lineitem,
    callback?: () => void
  ) => {
    let occupantsGuiids: number[] = [];
    const stay = reservation?.stays?.find((x) => x?.guiid === stayGuiid);
    if (stay) {
      //On parcours tout les occupants du stay
      for (let i = 0; i < (stay.occupants?.length ?? -1); i++) {
        const currentOccupant = stay?.occupants?.[i];
        if (currentOccupant) {
          //On parcours les lineitems de l'occupant courant;
          for (let j = 0; j < (currentOccupant?.lineItems?.length ?? -1); j++) {
            const currentLineItem = currentOccupant?.lineItems?.[j];
            if (currentLineItem) {
              if (
                currentLineItem?.product?.code ===
                  lineitemToRemove?.product?.code &&
                currentLineItem?.product?.otherCode ===
                  lineitemToRemove?.product?.otherCode
              ) {
                //Le lineitem envoyé au param a bien été trouvé;
                occupantsGuiids?.push(currentOccupant?.guiid!!);
              }
            }
          }
        }
      }
    }

    for (let i = 0; i < occupantsGuiids.length; i++) {
      //on remove pour
      const { data, errors } = await client.mutate<
        Mutation,
        MutationAddProductToOccupantArgs
      >({
        mutation: addProductToOccupantMutation(),
        variables: {
          session: { name: sessionId },
          input: [
            {
              occupantGuiid: occupantsGuiids[i],
              quantity: 0,
              productCode: lineitemToRemove.product?.code!!,
              productID: lineitemToRemove.product?.id!!,
            },
          ],
        },
      });

      if (
        data &&
        data.addProductToOccupant &&
        data?.addProductToOccupant?.cart &&
        !errors
      ) {
        if (i === occupantsGuiids?.length - 1) {
          callback && callback();
          setCart(data?.addProductToOccupant?.cart);
        }
      }
    }
  };

  //Le total partner prend en charge les produits de type commission
  const getTotalPartnerForClientB2B = () => {
    let total: number = reservation?.totalAmount?.amount ?? 0;
    for (let reservation of cart?.reservations ?? []) {
      for (let stay of reservation?.stays ?? []) {
        for (let occupant of stay?.occupants ?? []) {
          for (let lineItem of occupant?.lineItems ?? []) {
            if (COMMISSIONS_PRODUCTS.includes(lineItem?.product?.code ?? "")) {
              total -= lineItem?.totalAmount?.amount ?? 0;
            }
          }
        }
      }
    }

    return total;
  };

  return (
    <div>
      <div>
        <img
          className="tw-w-full tw-h-full tw-object-cover"
          style={{ maxHeight: "150px" }}
          src={reservation.property?.imagePath ?? ""}
          onError={(e) => replaceImgOnError(e)}
        ></img>
      </div>
      <div className="tw-p-2 tw-flex tw-flex-col">
        <div className="tw-mb-2">
          <div className="tw-flex tw-justify-between tw-items-center">
            <div className="tw-flex tw-items-center">
              <span className="tw-font-extrabold tw-text-lg tw-leading-5">
                {reservation.property?.address?.city}
              </span>
              <span className="tw-inline-block tw-mx-2"> - </span>
              <span className="tw-text-lg tw-leading-5">
                {reservation.property?.label}
              </span>
            </div>
          </div>
        </div>
        <div className="tw-w-2/3 tw-mx-auto">
          <Delimiter mode="block" />
        </div>
        <div className="tw-pt-4 tw-px-0 tw-text-lg">
          <span className="tw-block tw-lowercase">
            {dateFormat(new Date(reservation.startDate), "dd/mm/yy")}{" "}
            {getLanguageVarValue("LANG_neobooking_to_date")}{" "}
            {dateFormat(new Date(reservation.endDate), "dd/mm/yy")} -{" "}
            {reservation.nbDays} {getLanguageVarValue("LANG_neobooking_days")}
          </span>
          <span className="tw-lowercase">
            <NbOccupantByReservationTitle
              reservation={reservation}
              pluralAdultText={getLanguageVarValue("LANG_neobooking_adults")}
              singleAdultText={getLanguageVarValue("LANG_neobooking_adult")}
              pluralChildrenText={getLanguageVarValue(
                "LANG_neobooking_childrens"
              )}
              singleChildrenText={getLanguageVarValue(
                "LANG_neobooking_children"
              )}
              pluralBabyText={getLanguageVarValue("LANG_neobooking_babies")}
              singleBabyText={getLanguageVarValue("LANG_neobooking_baby")}
            />
          </span>
          <PopupEdit
            btnElement={(onClick) => (
              <button
                className="tw-font-dosis tw-underline tw-font-semibold tw-text-sm tw-pl-2 focus:tw-outline-none"
                onClick={onClick}
              >
                {getLanguageVarValue("LANG_neobooking_edit")}
              </button>
            )}
          />
        </div>
        <div className="tw-w-2/3 tw-mx-auto">
          <Delimiter mode="block" />
        </div>
        <div className="tw-py-4">
          {reservation?.stays?.map((stay, index) => {
            return (
              <React.Fragment key={`stay_${stay?.guiid}_${index}`}>
                <div className="tw-flex tw-flex-col tw-gap-2">
                  <div className="tw-flex tw-justify-between tw-gap-2">
                    <div>
                      {stay?.distribution?.roomTypes?.map((r, i) => {
                        return (
                          <div
                            key={`stay_${stay.guiid}_${index}_room_${r?.label}_${i}`}
                            className="tw-flex tw-justify-between tw-items-center tw-gap-2"
                          >
                            <span>
                              <span className="tw-font-bold tw-mr-2">
                                {r?.quantity}x {` `}
                              </span>{" "}
                              <span className="tw-text-lg">{r?.label}</span>
                            </span>
                          </div>
                        );
                      })}
                    </div>
                    <div className="tw-flex tw-gap-2 tw-items-center tw-justify-center">
                      <div className="tw-flex tw-items-center">
                        <span className="tw-font-semibold tw-text-lg">
                          {getBaseLineItem(
                            stay?.guiid!!
                          )?.totalAmountWithoutDiscount?.amount?.toFixed(2)}
                          {getCurrencySymbol(
                            getBaseLineItem(stay?.guiid!!)
                              ?.totalAmountWithoutDiscount?.currencyCode!!
                          )}
                        </span>
                      </div>
                      <ButtonRemoveStay guid={stay?.guiid!!}></ButtonRemoveStay>
                    </div>
                  </div>
                  <div>
                    <div
                      className={`tw-flex tw-gap-2 tw-justify-between tw-text-sm`}
                    >
                      <div>{getFormuleSejour()}</div>
                    </div>
                    <StayBaseDiscount stay={stay} />
                  </div>
                  <div>
                    {getDistinctLineItems(stay!!)?.map(
                      (lineitem: any, lineitemIndex: number) => {
                        return (
                          !lineitem?.product?.isBase &&
                          !TAXES_PRODUCTS.includes(
                            lineitem?.product?.code ?? ""
                          ) && (
                            <div
                              className="tw-py-2"
                              key={`lineitem_${lineitem?.guiid}_${lineitemIndex}`}
                            >
                              <LineItemRow
                                qte={lineitem?.qte}
                                totalPrice={lineitem?.totalPrice}
                                totalPriceWithoutDiscount={
                                  lineitem?.totalPriceWithoutDiscount
                                }
                                occupantGuiid={0}
                                occupantIndex={0}
                                stayGuiid={stay?.guiid!!}
                                lineitem={lineitem!!}
                                lineitemIndex={lineitemIndex}
                                handler2={handleRemoveLineItem}
                                selectedInsurance={selectedInsurance}
                                setSelectedInsurance={setSelectedInsurance}
                              />
                            </div>
                          )
                        );
                      }
                    )}
                  </div>
                  {reservation?.stays?.length !== index + 1 && (
                    <div className="tw-py-3">
                      <Delimiter />
                    </div>
                  )}
                </div>
              </React.Fragment>
            );
          })}
        </div>
        <div className="tw-w-2/3 tw-mx-auto">
          <Delimiter mode="block" />
        </div>
        {reservation.discountCode && (
          <div className="tw-py-4">
            <p className="tw-font-semibold tw-uppercase">
              {getLanguageVarValue("LANG_neobooking_reduction")}:
            </p>
            <div className="tw-flex tw-gap-2 tw-items-center tw-justify-between tw-text-xs tw-italic">
              <span className="tw-uppercase">
                {getLanguageVarValue("LANG_neobooking_your_code")}{" "}
                <span
                  className={`${
                    mode === "vacanceole"
                      ? "tw-text-[#f7b103]"
                      : "tw-text_corseole-danger"
                  }`}
                >
                  {reservation.discountCode}
                </span>{" "}
                {getLanguageVarValue(
                  "LANG_neobooking_your_code_successfully_applied"
                )}
              </span>
              {enableEdit && (
                <Button
                  variant="default"
                  isLoading={isRemovingCode}
                  shape="link"
                  type="button"
                  onClick={removeDiscountCode}
                  title={getLanguageVarValue(
                    "LANG_neobooking_remove_reduction"
                  )}
                >
                  <CloseOutlined fontSize="small" />
                </Button>
              )}
            </div>
          </div>
        )}
        {totalTax && totalTax > 0 ? (
          <div className="tw-py-2 tw-text-sm tw-flex tw-justify-between tw-mt-2 tw-italic">
            <div>{getLanguageVarValue("LANG_neobooking_total_tax")} </div>
            <div className="tw-flex tw-font-semibold">
              {getLanguageVarValue("LANG_neobooking_pre_reserved_pay_on_site")}{" "}
              {totalTax?.toFixed(2)}
              {getCurrencySymbol(reservation.totalAmount?.currencyCode!!)}
              <div className="tw-w-7"></div>
            </div>
          </div>
        ) : (
          <></>
        )}
        <div className="tw-py-4">
          <div className="tw-flex tw-justify-end tw-items-end tw-gap-1">
            <p className="tw-uppercase">
              {getLanguageVarValue(
                authMode === 2
                  ? "LANG_neobooking_total_partner_for_b2b"
                  : "LANG_neobooking_total"
              )}
              :
            </p>
            <p className="tw-font-semibold tw-text-2xl md:tw-mr-[28px] tw-leading-[1.8rem]">
              {reservation?.totalAmount?.amount?.toFixed(2)}
              {getCurrencySymbol(reservation.totalAmount?.currencyCode!!)}
            </p>
          </div>
          {authMode === 2 ? (
            <div className="tw-flex tw-justify-end tw-items-end tw-gap-1 tw-mt-2">
              <p className="tw-uppercase">
                {getLanguageVarValue("LANG_neobooking_total_client_for_b2b")}:
              </p>
              <p className="tw-font-semibold tw-text-2xl md:tw-mr-[28px] tw-leading-[1.8rem]">
                {getTotalPartnerForClientB2B()?.toFixed(2)}
                {getCurrencySymbol(reservation.totalAmount?.currencyCode!!)}
              </p>
            </div>
          ) : (
            <></>
          )}
          <TotalBaseDiscount reservation={reservation} />
          {getFidelityTotal().total > 0 && (
            <>
              <div className="tw-text-right md:tw-mr-[28px]">
                {getLanguageVarValue("LANG_neobooking_label_total_to_pay")}{" "}
                {getFidelityTotal().total}
                {getCurrencySymbol(getFidelityTotal().currencyCode)}
              </div>
              <div className="tw-uppercase tw-text-right md:tw-mr-[28px] tw-mt-4">
                {getLanguageVarValue("LANG_neobooking_total_to_pay")}{" "}
                <span className="tw-font-semibold tw-text-2xl tw-leading-[1.8rem]">
                  {getTotalWithFidelity().toFixed(2)}
                  {getCurrencySymbol(reservation.totalAmount?.currencyCode)}
                </span>
              </div>
            </>
          )}
        </div>
      </div>
      <div className="tw-py-2">
        <div className="tw-px-2">
          <div className="tw-flex tw-flex-col">
            {authMode !== 2 ? (
              <div className="tw-flex tw-items-center">
                <img
                  className="tw-inline-block tw-h-auto tw-max-h-[20px] tw-w-[20px] tw-mr-2"
                  src={getAbsoluteImagePath(
                    "/assets/img/icons/padlock-green.svg"
                  )}
                ></img>
                <div className="tw-font-semibold">
                  {getLanguageVarValue("LANG_neobooking_payment_secure")}
                </div>
              </div>
            ) : (
              <></>
            )}

            {authMode !== 2 && showPaiement3X() ? (
              <div className="tw-flex tw-items-center">
                <CheckCircleOutlineOutlined
                  fontSize="small"
                  className="tw-text-[#7ba53b] tw-mr-2 tw-w-[20px]"
                />
                <div>
                  {(reservation?.stays?.[0]?.occupants?.[0]?.paymentDues
                    ?.length ?? 0) > 0 ? (
                    <span className="tw-font-semibold">
                      {getLanguageVarValue(getPaymentX())} :{" "}
                    </span>
                  ) : (
                    <></>
                  )}
                  <span>{getLanguageVarValue("LANG_neobooking_pay_only")}</span>
                  <span className="tw-font-semibold">
                    {" "}
                    {
                      reservation?.stays?.[0]?.occupants?.[0]?.paymentDues?.[0]
                        ?.amount?.amount
                    }
                    {getCurrencySymbol(
                      reservation?.stays?.[0]?.occupants?.[0]?.paymentDues?.[0]
                        ?.amount?.currencyCode
                    )}{" "}
                  </span>
                  <span>
                    {getLanguageVarValue("LANG_neobooking_on_the_reservation")}
                  </span>
                </div>
              </div>
            ) : (
              <></>
            )}
            {/*authMode !== 2 && getCancelationDateLimit() !== null ? (
              <div className="tw-flex tw-items-center">
                <CheckCircleOutlineOutlined
                  fontSize="small"
                  className="tw-text-[#7ba53b] tw-mr-2 tw-w-[20px]"
                />
                <div>
                  <span className="tw-font-semibold">
                    {getLanguageVarValue("LANG_neobooking_free_cancelation")}
                  </span>{" "}
                  <span className="tw-lowercase">
                    {getLanguageVarValue("LANG_neobooking_until")}{" "}
                    {dateFormat(getCancelationDateLimit()!!, "ddd dd mmm yyyy")}
                  </span>
                </div>
              </div>
            ) : (
              <></>
            )*/}
          </div>
        </div>
      </div>
      {authMode !== 2 ? (
        <div className="tw-p-2 tw-flex tw-justify-center md:tw-justify-between tw-gap-1 tw-flex-wrap">
          <img
            title="Visa"
            className="tw-mx-2 tw-w-[11%] tw-min-w-[40px]"
            src={getAbsoluteImagePath("/assets/img/icons/cards/visa.svg")}
          ></img>
          <img
            title="Mastercard"
            className="tw-mx-2 tw-w-[11%] tw-min-w-[40px]"
            src={getAbsoluteImagePath("/assets/img/icons/cards/mastercard.svg")}
          ></img>
          <img
            title="Maestro"
            className="tw-mx-2 tw-w-[11%] tw-min-w-[40px]"
            src={getAbsoluteImagePath("/assets/img/icons/cards/maestro.svg")}
          ></img>
          <img
            title="AMEX"
            className="tw-mx-2 tw-w-[11%] tw-min-w-[40px]"
            src={getAbsoluteImagePath("/assets/img/icons/cards/amex.svg")}
          ></img>
          <img
            title="Paypal"
            className="tw-mx-2 tw-w-[11%] tw-min-w-[40px]"
            src={getAbsoluteImagePath("/assets/img/icons/cards/paypal.svg")}
          ></img>
          <img
            title="ANCV"
            className="tw-mx-2 tw-w-[11%] tw-min-w-[40px] tw-object-contain"
            src={getAbsoluteImagePath("/assets/img/icons/cards/ancv.jpg")}
          ></img>
        </div>
      ) : (
        <></>
      )}
    </div>
  );
};

type ButtonRemoveStayProps = {
  guid: number;
};

const ButtonRemoveStay: React.FC<ButtonRemoveStayProps> = ({ guid }) => {
  /* Hooks */
  const { removeStay } = useCart();

  /* State */
  const [loading, setIsLoading] = useState(false);

  /* Function */
  const handler = () => {
    setIsLoading(true);
    removeStay(guid, true, (err) => {
      if (err) toast.error(err.message);
      setIsLoading(false);
    });
  };

  /* Render */
  return (
    <Button
      variant="default"
      isLoading={loading}
      disabled={loading}
      shape="link"
      type="button"
      onClick={handler}
      additionalClassname="tw-flex tw-items-center"
    >
      <DeleteOutlined fontSize="small" />
    </Button>
  );
};

type PopupEditProps = {
  btnElement: (onClick: () => void) => JSX.Element;
};

const PopupEditLanguagesVarsCodes = [
  "LANG_neobooking_occupant_adding_in_progress",
  "LANG_neobooking_occupant_add",
  "LANG_neobooking_occupant_successfully_added",
  "LANG_neobooking_occupant_baby_successfully_added",
  "LANG_neobooking_occupant_baby_adding_in_progress",
];

export const PopupEdit: React.FC<PopupEditProps> = ({ btnElement }) => {
  /* Hooks */
  const { getLanguageVarValue } = useSession();
  const { cart } = useCart();

  return (
    <Popup btnElement={btnElement} title="">
      {cart?.reservations?.map((resa, resaIndex) => {
        return (
          <div
            key={`resa_${resa?.cartItemID}_i_${resaIndex}`}
            className="tw-flex tw-flex-col tw-gap-4"
          >
            {resa?.stays?.map((stay, stayIndex) => {
              return (
                <div
                  className="tw-flex tw-flex-col tw-gap-4"
                  key={`stay_${stay?.guiid!!}_i_${stayIndex}_occupants`}
                >
                  {!isEmpty(resa?.stays) && resa?.stays?.length!! > 1 ? (
                    <div className="tw-titre-principal-dosis">
                      {!isEmpty(stay?.distribution?.roomTypes) &&
                        stay?.distribution?.roomTypes?.[0]?.label}
                    </div>
                  ) : (
                    <div className="tw-titre-principal-dosis">
                      {getLanguageVarValue(
                        "LANG_neobooking_occupants_fill_occupants"
                      )}
                    </div>
                  )}
                  <StayOccupants
                    showProducts={false}
                    cartItemID={resa.cartItemID!!}
                    stay={stay!!}
                    etabName={resa.property?.label!!}
                    btnAddOccupantText={getLanguageVarValue(
                      "LANG_neobooking_occupant_add"
                    )}
                    loadingAddingOccupantText={getLanguageVarValue(
                      "LANG_neobooking_occupant_adding_in_progress"
                    )}
                    successfullyOccupantAddedText={getLanguageVarValue(
                      "LANG_neobooking_occupant_successfully_added"
                    )}
                    productListTitle={getLanguageVarValue(
                      "LANG_neobooking_product_global_title"
                    )}
                    btnAddBabyText={getLanguageVarValue(
                      "LANG_neobooking_occupant_baby_add"
                    )}
                    successfullyBabyAddedText={getLanguageVarValue(
                      "LANG_neobooking_occupant_baby_successfully_added"
                    )}
                    loadingAddingBabyText={getLanguageVarValue(
                      "LANG_neobooking_occupant_baby_adding_in_progress"
                    )}
                  />
                </div>
              );
            })}
          </div>
        );
      })}
    </Popup>
  );
};

export default BasketSummary;
